import React, { useState, useEffect, useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'

import FirstTime from './Common/Copy'
import NameTrip from './Common/Fields/NameTrip'
import TripSelect from './ModalChildren/TripSelect'

import { saveToTripEvent } from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import AnalyticsPageContext from 'contexts/Analytics/analyticsPageContext'
import useToggle from 'hooks/useToggle'
import type {
  BookmarkError,
  BookmarkRequest,
  BookmarkResponse,
} from 'tripPlanner/api/bookmark/types'
import BaseBookmarkButton from 'tripPlanner/components/Bookmark/Common/BaseBookmarkButton'
import { useDeleteTripItems } from 'tripPlanner/hooks/api/tripItem'
import { useCreateTrip } from 'tripPlanner/hooks/api/trip'
import { useProcessBookmarks } from 'tripPlanner/hooks/api/bookmark'
import type { BasicTrip, FullTrip } from 'tripPlanner/types/common'
import {
  getTripItemIdsInCheckout,
  tripHasItemKeys,
} from 'tripPlanner/utils/bookmarks/common'

type PopUpState = 'create' | 'select'

interface Props {
  payloads: BookmarkRequest['items']
  savedItemIds: Array<string>
  trips: Array<BasicTrip>
  newTripImageId: string
  newTripName?: string
  // Until trip multi-save, we'll need keys, this is currently an abstraction leak, it should ideally only accept payloads
  keys: Array<string>
  focusedTrip?: BasicTrip
  saveLabel?: string
  onSaveSuccess: (
    data: BookmarkResponse['result'],
    savedTrip: BasicTrip,
  ) => void
  onSaveError: (tripName: string, data: BookmarkError) => void
  onRemoveSuccess: (removedTrip: BasicTrip) => void
  onRemoveError: (tripName: string) => void
}

function MultiSaveButton({
  payloads,
  focusedTrip,
  trips,
  keys,
  savedItemIds,
  newTripImageId,
  newTripName,
  saveLabel = 'Save',
  onSaveError,
  onRemoveError,
  onRemoveSuccess,
  onSaveSuccess,
}: Props) {
  const intl = useIntl()
  const [tripName, setTripName] = useState(newTripName ?? '')
  const [errorMsg, setErrorMsg] = useState('')
  const [modalView, setModalView] = useState<PopUpState>('select')
  const analyticsPage = useContext(AnalyticsPageContext)

  const {
    value: isOpen,
    toggle: toggleModal,
    off: closeModal,
  } = useToggle(false)

  const { mutate: processBookmarks, isLoading: isProcessingBookmarks } =
    useProcessBookmarks({
      onSuccess: (data, _, trip) => {
        if (data.created.length > 0 || data.updated.length > 0) {
          if (trip) {
            onSaveSuccess(data, trip)
          }
          closeModal()
        }
      },
      onError: (e, vars, trip) => {
        if (vars.tripIdsRemoved.length > 0) {
          if (trip) {
            onRemoveError(trip.name)
          }
          closeModal()
        }

        if (vars.tripIdsAdded.length > 0) {
          if (trip) {
            onSaveError(trip.name, e)
          }
          closeModal()
        }
      },
    })

  const toggle = useCallback(
    (e?: React.MouseEvent<HTMLButtonElement>) => {
      e?.preventDefault()
      e?.stopPropagation()
      toggleModal()
    },
    [toggleModal],
  )

  const { mutate: deleteItems, isLoading: isDeletingItems } =
    useDeleteTripItems({
      onSuccess: (_, __, trip) => onRemoveSuccess(trip!),
      onError: (data, vars, trip) => onRemoveError(trip!.name),
    })

  useEffect(() => {
    if (isOpen) {
      setModalView(trips.length > 0 ? 'select' : 'create')
    }
  }, [isOpen, trips])

  const { mutate: createTrip, isLoading: isCreatingTrip } = useCreateTrip({
    onError: (e) => {
      if (e.code === 'TripNameConflict') {
        setErrorMsg(e.message)
      } else {
        setErrorMsg(
          intl.formatMessage({
            id: '/JEWmGsQSc',
            defaultMessage: 'Looks like there was an error! Try again or call us.',
            description: 'Error message when save operation fails',
          }),
        )
      }
    },
    onSuccess: (trip) => saveItemsToTrip(trip),
  })

  const createNewTrip = useCallback(async() => {
    createTrip({ name: tripName, imageId: newTripImageId })
  }, [createTrip, newTripImageId, tripName])

  const saveItemsToTrip = useCallback(
    (trip: BasicTrip | FullTrip) => {
      processBookmarks({
        items: payloads,
        tripIdsAdded: trip ? [trip.id] : [],
        tripIdsRemoved: [],
      })
    },
    [processBookmarks, payloads],
  )

  const onButtonClick = useCallback(() => {
    if (trips.length > 0 && focusedTrip) {
      if (savedItemIds.length > 0) {
        // Until trip multi-save, we're removing bookmarked offers from all trips, this is why we need 'keys'
        // This will only happen when the same offer, saved to more than 2 trips, has the same dates in all of them
        trips
          .filter((trip) => tripHasItemKeys(trip, keys))
          .forEach((trip) =>
            deleteItems({
              itemIds: getTripItemIdsInCheckout(keys, trip.plannedIdSets),
              tripId: trip.id,
            }),
          )
      } else {
        fireInteractionEvent(
          saveToTripEvent('button', 'immersive', 'click', analyticsPage),
        )
        saveItemsToTrip(focusedTrip)
      }
    } else {
      // There's no trips with these items saved to it
      fireInteractionEvent(
        saveToTripEvent('button', 'global', 'click', analyticsPage),
      )
      toggleModal()
    }
  }, [
    toggleModal,
    focusedTrip,
    saveItemsToTrip,
    savedItemIds,
    trips,
    keys,
    deleteItems,
    analyticsPage,
  ])

  const saveToTripTitle = intl.formatMessage({
    id: 'o5kdy75ZMV',
    defaultMessage: 'Save to trip',
    description: 'Title for save to trip modal',
  })

  const saveOrderTitle = intl.formatMessage({
    id: 'sDTdMSfpoo',
    defaultMessage: 'Save this order so we can help plan your next trip',
    description: 'Title for save order modal when no trips exist',
  })

  const title = trips.length > 0 ? saveToTripTitle : saveOrderTitle

  const saveButtonText = intl.formatMessage({
    id: 'zYWoNbZfWv',
    defaultMessage: 'Save',
    description: 'Button text to save',
  })

  const cancelButtonText = intl.formatMessage({
    id: '0ntkqedV1e',
    defaultMessage: 'Cancel',
    description: 'Button text to cancel',
  })

  const savedLabel = intl.formatMessage({
    id: 'IF9wcFD7x6',
    defaultMessage: 'Saved',
    description: 'Label text when item is saved',
  })

  const savingNotice = intl.formatMessage({
    id: 'KQ9pEhOWnD',
    defaultMessage: 'Saving this order does not hold the booking. Price and availability are subject to change.',
    description: 'Notice about saving order not holding booking',
  })

  const isProcessing =
    isCreatingTrip || isProcessingBookmarks || isDeletingItems

  const hasTrips = trips.length > 0

  return (
    <>
      <Modal
        title={title}
        isOpen={isOpen}
        onClose={closeModal}
        primaryActionText={modalView === 'create' ? saveButtonText : undefined}
        secondaryActionText={modalView === 'create' ? cancelButtonText : undefined}
        onPrimaryActionClick={
          modalView === 'create' ? createNewTrip : undefined
        }
        onSecondaryActionClick={modalView === 'create' ? toggle : undefined}
        isPrimaryActionDisabled={isProcessing || tripName.length === 0}
        isSecondaryActionDisabled={isProcessing}
        size={modalView === 'create' && !hasTrips ? 'L' : 'S'}
      >
        <VerticalSpacer gap={24}>
          <BodyText variant="large">
            {savingNotice}
          </BodyText>
          {trips.length === 0 && <FirstTime />}
          {modalView === 'select' && (
            <TripSelect
              onCreateSelect={() => setModalView('create')}
              onSelectTrip={saveItemsToTrip}
              trips={trips}
            />
          )}
          {modalView === 'create' && (
            <NameTrip
              firstTime={false}
              errorMsg={errorMsg}
              setErrorMsg={setErrorMsg}
              setTripName={setTripName}
              tripName={tripName}
              onSubmit={createNewTrip}
            />
          )}
        </VerticalSpacer>
        {isProcessing && <LoadingIndicator floating />}
      </Modal>
      <BaseBookmarkButton
        onClick={onButtonClick}
        label={savedItemIds.length > 0 ? savedLabel : saveLabel}
        isSaved={savedItemIds.length > 0}
        isProcessing={isProcessing}
      />
    </>
  )
}

export default MultiSaveButton
